// ─── El vínculo cuenta ↔ persona del diálogo de /usuarios (puro, con test) ───
// Antes, el diálogo armaba la persona vinculada desde una lista de `people`
// con `limit(500)`. En un seminario con más personas, la vinculada podía no
// estar en la lista: el diálogo la mostraba vacía, y GUARDAR la desvinculaba y
// borraba sus cargos institucionales. Nadie lo había pedido.
//
// INVARIANTE: guardar nunca desvincula una persona ni toca cargos que el
// diálogo no llegó a mostrar. La persona vinculada se lee por id, siempre; si no
// se pudo leer, el vínculo y los cargos no se envían.

export interface PersonOption {
  id: string;
  label: string;
  email: string;
  institutionalRoles: string[];
  /** Nombre + código + correo, normalizado, para filtrar en el cliente. */
  haystack: string;
}

/** Lo que se sabe de la persona vinculada al abrir el diálogo. */
export type LinkedPerson =
  /** Todavía no se sabe: el diálogo recién se abrió (o cambió de cuenta). */
  | { state: 'loading' }
  | { state: 'none' }
  | { state: 'loaded'; option: PersonOption }
  /** Tiene vínculo, pero la persona no se pudo leer (error o documento ausente). */
  | { state: 'unreadable' };

/** Las opciones del selector de persona. */
export type PersonListState =
  | { mode: 'loading' }
  /** Hasta PERSON_LIST_CAP personas: la lista completa, como siempre. */
  | { mode: 'list'; options: PersonOption[] }
  /** Más personas: el selector busca. */
  | { mode: 'search' }
  /** La lista no se pudo leer: el vínculo leído por id se sigue mostrando. */
  | { mode: 'unavailable' };

/** El estado del diálogo al abrirse para UNA cuenta. Nada de la cuenta
 *  anterior sobrevive: el diálogo no se vuelve a montar al cambiar de cuenta, y
 *  guardar con el vínculo y los cargos de otra cuenta rompía la invariante. */
export interface DialogSelection {
  linked: LinkedPerson;
  list: PersonListState;
  selectedPerson: PersonOption | null;
  selectedInstitutional: string[];
}

export function openDialogState(): DialogSelection {
  return {
    linked: { state: 'loading' },
    list: { mode: 'loading' },
    selectedPerson: null,
    selectedInstitutional: [],
  };
}

/** Guardar solo cuando se sabe lo que se está guardando. */
export function canSave(input: {
  linked: LinkedPerson;
  list: PersonListState;
  saving: boolean;
  selectedRoles: readonly string[];
}): boolean {
  return (
    !input.saving &&
    input.selectedRoles.length > 0 &&
    input.linked.state !== 'loading' &&
    input.list.mode !== 'loading'
  );
}

/** Las opciones que ve el selector: la lista, o lo encontrado, o nada si la
 *  lista no se pudo leer; nunca las ya vinculadas a OTRA cuenta; y la elegida
 *  siempre presente. */
export function selectorOptions(input: {
  list: PersonListState;
  searchResults: readonly PersonOption[];
  takenPersonIds: ReadonlySet<string>;
  selectedPerson: PersonOption | null;
}): PersonOption[] {
  const base =
    input.list.mode === 'list'
      ? input.list.options
      : input.list.mode === 'search'
        ? input.searchResults
        : [];
  const options = base.filter((o) => !input.takenPersonIds.has(o.id));
  if (input.selectedPerson && !options.some((o) => o.id === input.selectedPerson!.id)) {
    options.unshift(input.selectedPerson);
  }
  return options;
}

/** Hasta cuántas personas el selector trae la lista completa, como siempre. */
export const PERSON_LIST_CAP = 500;
/** Con más personas, el selector busca: como mucho esto por consulta. */
export const PERSON_SEARCH_LIMIT = 20;

/** Minúsculas y sin tildes: «José» se encuentra escribiendo «jose». */
export function normalizeSearch(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

export function toPersonOption(id: string, data: Record<string, unknown>): PersonOption {
  const first = (data['firstName'] as string | undefined) ?? '';
  const last = (data['lastName'] as string | undefined) ?? '';
  const email = (data['email'] as string | undefined) ?? '';
  const code = (data['studentCode'] as string | undefined) ?? '';
  const full = `${first} ${last}`.trim() || email || id;
  return {
    id,
    label: email ? `${full} (${email})` : full,
    email,
    institutionalRoles: Array.isArray(data['institutionalRoles'])
      ? (data['institutionalRoles'] as string[])
      : [],
    haystack: normalizeSearch(`${first} ${last} ${code} ${email}`),
  };
}

/** La lista completa (seminario chico): misma deduplicación que siempre. */
export function toPersonOptions(
  docs: ReadonlyArray<{ id: string; data: Record<string, unknown> }>,
): PersonOption[] {
  const seenIds = new Set<string>();
  const seenEmails = new Set<string>(); // mismo correo → misma persona
  const seenNames = new Set<string>(); // mismo nombre y sin correo → misma persona
  const results: PersonOption[] = [];
  for (const d of docs) {
    if (seenIds.has(d.id)) continue;
    seenIds.add(d.id);
    const option = toPersonOption(d.id, d.data);
    const emailKey = option.email.toLowerCase();
    if (option.email && seenEmails.has(emailKey)) continue;
    if (option.email) seenEmails.add(emailKey);
    const nameKey = option.label.toLowerCase();
    if (!option.email && seenNames.has(nameKey)) continue;
    if (!option.email) seenNames.add(nameKey);
    results.push(option);
  }
  return results.sort((a, b) => a.label.localeCompare(b.label));
}

/** Las consultas de prefijo que resuelven una búsqueda, sin traer todo.
 *  Firestore distingue mayúsculas: se prueba el texto como viene, con
 *  mayúscula inicial y en minúsculas. Un código va a `studentCode`. */
export interface PrefixQuery {
  field: 'lastName' | 'firstName' | 'email' | 'studentCode';
  prefix: string;
}

export function searchQueries(text: string): PrefixQuery[] {
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  // El más largo es el más selectivo; el resto filtra en el cliente.
  const token = [...tokens].sort((a, b) => b.length - a.length)[0]!;
  const out: PrefixQuery[] = [];
  const code = /^l-?(\d+)$/i.exec(token) ?? /^(\d{3,})$/.exec(token);
  if (code) out.push({ field: 'studentCode', prefix: `L-${code[1]}` });
  if (token.includes('@')) {
    out.push({ field: 'email', prefix: token.toLowerCase() });
    return out;
  }
  if (code) return out;
  const variants = [
    ...new Set([
      token,
      token.charAt(0).toUpperCase() + token.slice(1).toLowerCase(),
      token.toLowerCase(),
    ]),
  ];
  for (const field of ['lastName', 'firstName'] as const) {
    for (const prefix of variants) out.push({ field, prefix });
  }
  return out;
}

/** Todas las palabras escritas aparecen en nombre, código o correo. */
export function matchesSearch(option: PersonOption, text: string): boolean {
  const tokens = normalizeSearch(text).split(/\s+/).filter(Boolean);
  return tokens.every((tk) => option.haystack.includes(tk));
}

/** Qué envía «Guardar». Aquí vive la invariante. */
export interface SavePlan {
  roles: string[];
  /** Ausente = el servidor no toca los cargos de la persona. */
  institutionalRoles?: string[];
  /** Ausente = el vínculo no se toca. `null` = desvincular (solo si se pidió). */
  personLink?: string | null;
}

export function planSave(input: {
  previousPersonId: string | null;
  linked: LinkedPerson;
  selectedPerson: PersonOption | null;
  selectedRoles: readonly string[];
  selectedInstitutional: readonly string[];
}): SavePlan {
  const roles = [...input.selectedRoles];
  // Mientras se lee, o si no se pudo leer, el diálogo no mostró ni el vínculo
  // ni los cargos: no se envía ninguno de los dos, haya o no vínculo previo.
  if (input.linked.state === 'loading' || input.linked.state === 'unreadable') return { roles };
  // Defensa: con vínculo previo, solo se toca si la persona se llegó a leer.
  if (input.previousPersonId && input.linked.state !== 'loaded') return { roles };
  const next = input.selectedPerson?.id ?? null;
  return {
    roles,
    institutionalRoles: [...input.selectedInstitutional],
    ...(next !== input.previousPersonId ? { personLink: next } : {}),
  };
}
